"use client";

import React from "react";
import { format } from "date-fns";
import { Eye, EyeOff } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { TimelineItem } from "@/lib/hooks/api/timeline/enhanced-timeline";

interface EmailOpenBadgeProps {
  openTracking: NonNullable<TimelineItem["open_tracking"]>;
  className?: string;
}

function safeFormat(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return format(d, "MMM d, yyyy 'at' h:mm a");
}

/**
 * "Opened" / "Not opened yet" indicator for a tracked email communication. The
 * hover tooltip explains what the signal means and its known limitations
 * (image-proxy preloads like Apple Mail Privacy Protection, image blocking) so
 * viewers don't over-trust it.
 */
export function EmailOpenBadge({ openTracking, className }: EmailOpenBadgeProps): React.ReactNode {
  const opened = Boolean(openTracking.openedAt);
  const firstOpen = safeFormat(openTracking.openedAt);
  const lastOpen = safeFormat(openTracking.lastOpenedAt);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          variant="secondary"
          className={`text-xs gap-1 ${
            opened
              ? "bg-green-100 text-green-800 hover:bg-green-100"
              : "bg-muted text-muted-foreground"
          } ${className ?? ""}`}
        >
          {opened ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
          {opened ? "Opened" : "Not opened yet"}
          {opened && openTracking.openCount > 1 ? ` ·  ${openTracking.openCount}×` : ""}
        </Badge>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs text-xs leading-relaxed">
        {opened ? (
          <div className="space-y-1">
            <div className="font-medium">Recipient opened this email</div>
            {firstOpen && <div>First opened: {firstOpen}</div>}
            {lastOpen && openTracking.openCount > 1 && (
              <div>
                Last of {openTracking.openCount} opens: {lastOpen}
              </div>
            )}
          </div>
        ) : (
          <div className="font-medium">No open recorded yet</div>
        )}
        <div className="mt-2 border-t border-border/60 pt-2 text-muted-foreground">
          We detect opens with an invisible tracking pixel. This is an approximate
          signal: some mail apps (notably Apple Mail Privacy Protection) preload
          images and can mark an email opened even when unread, while apps that
          block images never report an open.
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
